import { useState } from "react";
import { User, Phone, ShieldCheck, Gift, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";

const validate = ({ nombre, telefono }) => {
  const errors = {};
  const cleanName = nombre.trim().replace(/\s+/g, " ");
  if (cleanName.length < 3) errors.nombre = "Escribe tu nombre y apellido";
  else if (!/^[\p{L}\p{M}'’ .-]+$/u.test(cleanName)) errors.nombre = "Solo letras, por favor";
  const digits = telefono.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15) errors.telefono = "Ingresa un teléfono válido de 10 dígitos";
  return errors;
};

const formatPhone = (value) => {
  const d = value.replace(/\D/g, "").slice(0, 15);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return `+${d.slice(0, d.length - 10)} (${d.slice(-10, -7)}) ${d.slice(-7, -4)}-${d.slice(-4)}`;
};

export const LeadForm = ({ onSuccess, compact = false }) => {
  const [form, setForm] = useState({ nombre: "", telefono: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const lead = await createLead({ nombre: form.nombre.trim(), telefono: form.telefono });
      setForm({ nombre: "", telefono: "" });
      onSuccess(lead);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg?.replace("Value error, ", "") : null;
      const text = msg || "No pudimos enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo.";
      setErrors({ form: text });
      toast.error(text);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="lead-form">
      <div>
        <label htmlFor="nombre" className="block text-sm font-semibold text-navy mb-1.5">
          Nombre y apellido
        </label>
        <div className="relative">
          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            id="nombre"
            name="nombre"
            type="text"
            autoComplete="name"
            placeholder="Ej. Carlos Mendoza"
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            className={`field ${errors.nombre ? "field-error" : ""}`}
            data-testid="lead-input-name"
          />
        </div>
        {errors.nombre && (
          <p className="mt-1.5 text-xs font-medium text-red-600" data-testid="lead-error-name">
            {errors.nombre}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="telefono" className="block text-sm font-semibold text-navy mb-1.5">
          Número de teléfono
        </label>
        <div className="relative">
          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            id="telefono"
            name="telefono"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="Ej. (555) 000-0000"
            value={form.telefono}
            onChange={(e) => setForm({ ...form, telefono: formatPhone(e.target.value) })}
            className={`field ${errors.telefono ? "field-error" : ""}`}
            data-testid="lead-input-phone"
          />
        </div>
        {errors.telefono && (
          <p className="mt-1.5 text-xs font-medium text-red-600" data-testid="lead-error-phone">
            {errors.telefono}
          </p>
        )}
      </div>

      {errors.form && (
        <p className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm font-medium text-red-700" data-testid="lead-error-form">
          {errors.form}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="btn-cta w-full rounded-xl py-4 text-base sm:text-lg font-extrabold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        data-testid="lead-submit-button"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
        {loading ? "Enviando..." : "Quiero mi cotización gratis"}
      </button>

      {!compact && (
        <div className="flex items-start gap-2 rounded-xl bg-[var(--gold-50)] border border-amber-200 p-3">
          <Gift className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-amber-900 leading-snug">
            <span className="font-bold">Bono inmediato:</span> al enviar recibes tu vale de{" "}
            <span className="font-extrabold">$50 de descuento</span> para tu seguro de auto.
          </p>
        </div>
      )}

      <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
        <Lock className="h-3 w-3" /> Tus datos están protegidos. Sin compromiso, sin spam.
      </p>
    </form>
  );
};
